// Hybrid Lineage Component
// - Layout: Cytoscape + Dagre (автоматическая расстановка)
// - Rendering: JointJS (таблицы с колонками и портами)
// - UX: Zoom/pan через координаты

import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, Input } from '@angular/core';
import { SourceService } from '../../data-access-layer/services/sources.service';
import { UserSessionStorageService } from '../../data-access-layer/services/user-sessions.service';
import { PythonService } from '../../data-access-layer/services/python.service';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import * as joint from 'jointjs';
import cytoscape from 'cytoscape';
// @ts-ignore
import dagre from 'cytoscape-dagre';

// Register Cytoscape dagre extension
cytoscape.use(dagre);

// Type definitions
interface ColumnData {
  id: number;
  name: string;
  type: string;
}

interface TableNodeData {
  id: string;
  objectId: number;
  label: string;
  schema: string;
  objectType: string;
  columns: ColumnData[];
  isMain: boolean;
}

interface ColumnLinkData {
  sourceTableId: string;
  sourceColumnId: number;
  targetTableId: string;
  targetColumnId: number;
  parentData?: {
    id: string;
    name: string;
    type: string;
    schema: string;
  };
}

interface TableLinkData {
  sourceTableId: string;
  targetTableId: string;
  parentData?: {
    id: string;
    name: string;
    type: string;
    schema: string;
  };
}

interface LineageData {
  tables: TableNodeData[];
  tableLinks: TableLinkData[];
  columnLinks: ColumnLinkData[];
}

@Component({
  selector: 'app-lineage',
  templateUrl: '../templates/lineage.component.html',
  styleUrls: ['../templates/styles/lineage.component.css'],
  standalone: false
})
export class LineageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cyContainer', { static: true }) cyContainer!: ElementRef;

  @Input() dataSourceId: number = 0;
  @Input() schemaId: number = 0;
  @Input() tableId: number = 0;

  private graph!: joint.dia.Graph;
  private paper!: joint.dia.Paper;

  showColumns: boolean = true;
  selectedNode: any = null;
  isToolbarCollapsed: boolean = false;
  isBI: boolean = false;

  public selectedParentInfo: { id?: string; name: string; type: string; schema?: string } | null = null;
  public isShowBI: boolean = false;
  public isShowUnknown: boolean = true;
  public nForward: number = 3;
  public nBackward: number = 3;

  public elementType: any;
  public currentElement: any;

  // Column lineage storage
  private allColumnLinks: ColumnLinkData[] = [];
  private createdLinks: Map<string, joint.shapes.standard.Link> = new Map();
  public selectedColumn: { tableId: string; columnId: number; columnName: string } | null = null;
  public linkDisplayMode: 'none' | 'selected' | 'all' = 'none';

  // Zoom/pan state
  private zoomLevel: number = 1;
  private panX: number = 0;
  private panY: number = 0;

  // Flag to prevent lineage loading when clicking on column
  private columnClickInProgress: boolean = false;

  // State for expanded/collapsed nodes
  private expandedNodes: Set<string> = new Set();

  // Store current lineage data for re-rendering
  private currentLineageData: LineageData | null = null;

  public isLegend = false;

  constructor(
    private sourceService: SourceService, 
    private sessionService: UserSessionStorageService, 
    private pythonService: PythonService,
    private router: Router,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.elementType = this.sessionService.getItem('elementType');
    this.currentElement = this.sessionService.getItem('currentElement');
    const storedIsBI = this.sessionService.getItem('isBI');
    this.isBI = storedIsBI === 'true' || storedIsBI === true;
  }

  ngAfterViewInit(): void {
    this.initJointJS();
    this.loadFromApiAndBuild();
  }

  ngOnDestroy(): void {
    if (this.graph) {
      this.graph.clear();
    }
  }

  private initJointJS(): void {
    this.graph = new joint.dia.Graph();

    this.paper = new joint.dia.Paper({
      el: this.cyContainer.nativeElement,
      model: this.graph,
      width: '100%',
      height: '100%',
      gridSize: 1,
      background: { color: '#FFFFFF' },
      interactive: {
        elementMove: true,
        addLinkFromMagnet: false
      },
      async: true,
      sorting: joint.dia.Paper.sorting.APPROX,
      defaultLink: new joint.shapes.standard.Link({
        attrs: {
          line: {
            stroke: '#D0ADFF',
            strokeWidth: 2,
            targetMarker: {
              type: 'path',
              d: 'M 10 -5 0 0 10 5 z',
              fill: '#D0ADFF'
            }
          }
        },
        router: { name: 'rightAngle', args: { radius: 5} },
        connector: { name: 'rounded', args: { radius: 5 } }
      })
    });

    this.setupInteractions();
    this.setupZoomPan();
  }

  private setupZoomPan(): void {
    const container = this.cyContainer.nativeElement;

    // Mouse wheel zoom
    container.addEventListener('wheel', (evt: WheelEvent) => {
      evt.preventDefault();

      const delta = -evt.deltaY / 1000;
      const oldZoom = this.zoomLevel;
      const newZoom = Math.max(0.1, Math.min(5, this.zoomLevel * (1 + delta)));

      // Zoom towards mouse position
      const rect = container.getBoundingClientRect();
      const mouseX = evt.clientX - rect.left;
      const mouseY = evt.clientY - rect.top;

      // Точка под мышью в координатах модели (до зума)
      // screenX = modelX * scale + tx => modelX = (screenX - tx) / scale
      const modelX = (mouseX - this.panX) / oldZoom;
      const modelY = (mouseY - this.panY) / oldZoom;

      // После зума эта же точка модели должна быть под мышью
      // mouseX = modelX * newZoom + newPanX => newPanX = mouseX - modelX * newZoom
      this.panX = mouseX - modelX * newZoom;
      this.panY = mouseY - modelY * newZoom;
      this.zoomLevel = newZoom;

      this.applyTransform();
    });

    // Pan with mouse drag on blank area
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let startPanX = 0;
    let startPanY = 0;

    this.paper.on('blank:pointerdown', (evt: any) => {
      isPanning = true;
      startX = evt.clientX;
      startY = evt.clientY;
      startPanX = this.panX;
      startPanY = this.panY;
      container.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', (evt: MouseEvent) => {
      if (isPanning) {
        this.panX = startPanX + (evt.clientX - startX);
        this.panY = startPanY + (evt.clientY - startY);
        this.applyTransform();
      }
    });

    document.addEventListener('mouseup', () => {
      if (isPanning) {
        isPanning = false;
        container.style.cursor = 'default';
                  }
                });
              }

  private applyTransform(): void {
    this.paper.matrix({
      a: this.zoomLevel,
      b: 0,
      c: 0,
      d: this.zoomLevel,
      e: this.panX,
      f: this.panY
    });
  }

  public zoomIn(): void {
    this.zoomToCenter(1.2);
  }

  public zoomOut(): void {
    this.zoomToCenter(1 / 1.2);
  }

  private zoomToCenter(factor: number): void {
    const container = this.cyContainer.nativeElement;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2;

    const oldZoom = this.zoomLevel;
    const newZoom = Math.max(0.1, Math.min(5, this.zoomLevel * factor));

    // Точка в центре экрана в координатах модели
    const modelX = (centerX - this.panX) / oldZoom;
    const modelY = (centerY - this.panY) / oldZoom;

    // После зума центр экрана должен показывать ту же точку модели
    this.panX = centerX - modelX * newZoom;
    this.panY = centerY - modelY * newZoom;
    this.zoomLevel = newZoom;

    this.applyTransform();
  }

  public resetZoom(): void {
    this.centerContent();
  }

  private setupInteractions(): void {
    this.paper.on('element:pointerclick', (elementView: joint.dia.ElementView, evt: any) => {
      // Если был клик на столбец, не загружаем lineage
      if (this.columnClickInProgress) {
        this.columnClickInProgress = false;
        return;
      }

      const element = (elementView as any).model as joint.dia.Element;
      const elementId = element.id as string;
      const nodeLabel = element.attr('label/text');
      const objectType = element.prop('objectType');
      const schema = element.prop('schema');

      this.selectedNode = {
        id: elementId,
        label: nodeLabel,
        type: element.prop('nodeType'),
        typeDS: objectType,
        columns: element.prop('columnsList') || [],
        schema: schema
      };

      this.selectedParentInfo = null;
      this.highlightElement(element.id as string);

      // Не загружаем lineage для unknown объектов
      if (elementId.startsWith('unknown_') || objectType === 'Отсутствующий') {
        return;
      }

      // Load lineage for the selected node
      this.loadLineageForNode(elementId, objectType, nodeLabel, schema);
    });

    // Expand/collapse button click
    this.paper.on('element:expand:click', (elementView: joint.dia.ElementView, evt: any) => {
      this.columnClickInProgress = true;
      evt.stopPropagation();
      if (evt.preventDefault) evt.preventDefault();

      const element = (elementView as any).model as joint.dia.Element;
      const elementId = element.id as string;
      
      this.toggleNodeColumns(elementId);
    });

  this.paper.on('element:navigate:click', (elementView: joint.dia.ElementView, evt: any) => {
      this.columnClickInProgress = true;
      evt.stopPropagation();
      if (evt.preventDefault) evt.preventDefault();

      const element = (elementView as any).model as joint.dia.Element;
      const elementId = element.id as string;
      const objectType = element.prop('objectType');
      const nodeLabel = element.attr('label/text');
      const schema = element.prop('schema');
      const objectId = element.prop('objectId');

      // Не переходим для unknown объектов
      if (elementId.startsWith('unknown_') || objectType === 'Отсутствующий') {
        return;
      }

      this.navigateToContent(elementId, objectId, objectType, nodeLabel, schema);
    });

    // Column port clicks
    this.paper.on('element:port:pointerclick', (elementView: joint.dia.ElementView, evt: any) => {
      const element = (elementView as any).model as joint.dia.Element;
      const tableId = element.id as string;
      const portAttr = (elementView as any).findAttribute('port', evt.target);
      const port = portAttr ? element.getPort(portAttr) : null;

      if (port && port.id && port.id.startsWith('column_')) {
        // Устанавливаем флаг, чтобы element:pointerclick не загружал lineage
        this.columnClickInProgress = true;
        evt.stopPropagation();
        if (evt.preventDefault) evt.preventDefault();

        const columnId = parseInt(port.id.replace('column_', ''), 10);
        const columns = element.prop('columnsList') || [];
        const column = columns.find((c: ColumnData) => c.id === columnId);
        if (column) {
          this.showLinksForColumn(tableId, columnId, column.name);
        }
      }
    });

    // Fallback port click handler
    this.paper.on('cell:pointerclick', (cellView: joint.dia.CellView, evt: any) => {
      const target = evt.target;
      const portElement = target.closest('[port]');

      if (portElement && (cellView as any).model.isElement()) {
        const portId = portElement.getAttribute('port');
        const element = (cellView as any).model as joint.dia.Element;
        const tableId = element.id as string;

        if (portId && portId.startsWith('column_')) {
          // Устанавливаем флаг, чтобы element:pointerclick не загружал lineage
          this.columnClickInProgress = true;
          evt.stopPropagation();
          if (evt.preventDefault) evt.preventDefault();

          const columnId = parseInt(portId.replace('column_', ''), 10);
          const columns = element.prop('columnsList') || [];
          const column = columns.find((c: ColumnData) => c.id === columnId);
          if (column) {
            this.showLinksForColumn(tableId, columnId, column.name);
          }
        }
      }
    });

    this.paper.on('link:pointerclick', (linkView: joint.dia.LinkView) => {
      const link = (linkView as any).model as joint.dia.Link;
      const parentData = link.prop('parentData');

      this.selectedNode = null;

      if (parentData) {
        this.selectedParentInfo = {
          id: parentData.id,
          name: parentData.name || '',
          type: parentData.type || '',
          schema: parentData.schema || ''
        };
      } else {
        this.selectedParentInfo = null;
      }

      this.graph.getLinks().forEach(l => {
        if (l.id === link.id) {
          l.attr('line/stroke', '#7855ED');
          l.attr('line/strokeWidth', 3);
          l.attr('line/targetMarker/fill', '#7855ED');
        } else {
          const parentData = l.prop('parentData');
          l.attr('line/stroke', '#D0ADFF');
          l.attr('line/strokeWidth', 2);
          l.attr('line/targetMarker/fill', '#D0ADFF');
        }
      });

      const sourceId = link.source().id;
      const targetId = link.target().id;

      this.highlightLinkedNodes(sourceId as string, targetId as string);
    });

    this.paper.on('blank:pointerclick', () => {
      this.selectedNode = null;
      this.selectedParentInfo = null;
      this.resetHighlights();
    });
  }

  private loadLineageForNode(nodeId: string, objectType: string, nodeLabel: string, schema: string): void {
    let objectId: number | null = null;
    let finalObjectType = objectType.toLowerCase();
    
    // Определяем тип объекта и переключаем режим BI/DWH
    const biTypes = ['report'];
    const dwhTypes = ['table', 'view', 'procedure', 'function'];
    
    // Извлекаем ID из nodeId (может быть с префиксом или без)
    if (nodeId.startsWith('table_')) {
      objectId = parseInt(nodeId.replace('table_', ''), 10);
      finalObjectType = 'table';
    } else if (nodeId.startsWith('view_')) {
      objectId = parseInt(nodeId.replace('view_', ''), 10);
      finalObjectType = 'view';
    } else if (nodeId.startsWith('procedure_')) {
      objectId = parseInt(nodeId.replace('procedure_', ''), 10);
      finalObjectType = 'procedure';
    } else if (nodeId.startsWith('report_')) {
      objectId = parseInt(nodeId.replace('report_', ''), 10);
      finalObjectType = 'report';
    } else if (nodeId.startsWith('chart_')) {
      objectId = parseInt(nodeId.replace('chart_', ''), 10);
      finalObjectType = 'chart';
    } else {
      objectId = parseInt(nodeId, 10);
    }

    // Переключаем режим в зависимости от типа объекта
    if (biTypes.includes(finalObjectType)) {
      this.isBI = true;
    } else if (dwhTypes.includes(finalObjectType)) {
        this.isBI = false;
      }
    // Если тип неизвестен - оставляем текущий режим

    if (isNaN(objectId)) return;

          const newElement = {
            id: objectId,
            name: nodeLabel,
      type: finalObjectType.charAt(0).toUpperCase() + finalObjectType.slice(1),
      schema: schema || ''
          };
          this.currentElement = newElement;
    this.elementType = newElement.type;
          this.sessionService.setItem('currentElement', newElement);
    this.sessionService.setItem('elementType', newElement.type);
    this.sessionService.setItem('isBI', this.isBI);

          this.loadFromApiAndBuild({
      object_type: finalObjectType,
            object_id: objectId,
            n_forward: this.nForward,
            n_backward: this.nBackward,
            show_bi: this.isShowBI,
            show_unknown: this.isShowUnknown
          });
        }

  private highlightElement(elementId: string): void {
    this.graph.getElements().forEach(el => {
      if (el.id === elementId) {
        el.attr('body/stroke', '#7855ED');
        el.attr('body/strokeWidth', 4);
      } else {
        const isMain = el.prop('nodeType') === 'main';
        const isBI = el.prop('objectType') === 'report';
        el.attr('body/stroke', isBI ? '#2196F3' : (isMain ? '#7855ED' : '#E5E5E5'));
        el.attr('body/strokeWidth', isMain ? 3 : 2);
        el.attr('body/fill', isBI ? '#E3F2FD' : (isMain ? '#F2EEFF' : '#FFFFFF'));
      }
    });
  }

  private highlightLinkedNodes(sourceId: string, targetId: string): void {
    this.graph.getElements().forEach(el => {
      const elId = el.id as string;
      if (elId === sourceId) {
        el.attr('body/stroke', '#4CAF50');
        el.attr('body/strokeWidth', 3);
        el.attr('body/fill', '#E8F5E9');
      } else if (elId === targetId) {
        el.attr('body/stroke', '#FF9800');
        el.attr('body/strokeWidth', 3);
        el.attr('body/fill', '#FFF3E0');
      } else {
        const isMain = el.prop('nodeType') === 'main';
        const isUnknown = el.prop('objectType') === 'Отсутствующий';
        const isBI = el.prop('objectType') === 'report';
        el.attr('body/stroke', isUnknown ? 'red' : (isBI ? '#2196F3' : (isMain ? '#7855ED' : '#E5E5E5')));
        el.attr('body/strokeWidth', isMain ? 3 : 2);
        el.attr('body/fill', isUnknown ? '#ffebee' : (isBI ? '#E3F2FD' : (isMain ? '#F2EEFF' : '#FFFFFF')));
      }
    });
  }

  private resetHighlights(): void {
    this.graph.getElements().forEach(el => {
      const isMain = el.prop('nodeType') === 'main';
      const isUnknown = el.prop('objectType') === 'Отсутствующий';
      const isBI = el.prop('objectType') === 'report';
      el.attr('body/stroke', isUnknown ? 'red' : (isBI ? '#2196F3' : (isMain ? '#7855ED' : '#E5E5E5')));
      el.attr('body/strokeWidth', isMain ? 3 : 2);
      el.attr('body/fill', isUnknown ? '#ffebee' : (isBI ? '#E3F2FD' : (isMain ? '#F2EEFF' : '#FFFFFF')));
    });

    this.graph.getLinks().forEach(link => {
      const parentData = link.prop('parentData');
      link.attr('line/stroke', '#D0ADFF');
      link.attr('line/strokeWidth', 2);
      link.attr('line/targetMarker/fill', '#D0ADFF');
    });
  }

  // Column links methods
  private showLinksForColumn(tableId: string, columnId: number, columnName: string): void {
    this.selectedColumn = { tableId, columnId, columnName };
    this.linkDisplayMode = 'selected';
    this.isShowAllLinks = false;
    
    // Удаляем все предыдущие связи столбцов
    this.clearColumnLinks();

    const allRelevantLinks = this.collectAllRelatedLinks(tableId, columnId);

    // Собираем ID связанных объектов, которые нужно раскрыть
    const relatedNodeIds: Set<string> = new Set();
    allRelevantLinks.forEach(linkData => {
      relatedNodeIds.add(linkData.sourceTableId);
      relatedNodeIds.add(linkData.targetTableId);
    });

    // Раскрываем связанные объекты если они свёрнуты
    let needsRerender = false;
    relatedNodeIds.forEach(nodeId => {
      if (!this.expandedNodes.has(nodeId)) {
        this.expandedNodes.add(nodeId);
        needsRerender = true;
      }
    });

    // Если были раскрыты новые объекты, перерисовываем
    if (needsRerender && this.currentLineageData) {
      this.showColumns = true;
      this.renderLineage(this.currentLineageData);
      // После перерисовки вызываем себя снова для отображения связей
      this.showLinksForColumnAfterRender(tableId, columnId, columnName);
      return;
    }

    this.showLinksForColumnAfterRender(tableId, columnId, columnName);
  }

  // Рекурсивно собирает все связи для столбца и связанных с ним столбцов
  private collectAllRelatedLinks(
    startTableId: string,
    startColumnId: number
  ): ColumnLinkData[] {
    const visitedColumns = new Set<string>();
    const collectedLinks: ColumnLinkData[] = [];
    const linkKeys = new Set<string>();

    const collectRecursive = (tableId: string, columnId: number) => {
      const columnKey = `${tableId}:${columnId}`;
      if (visitedColumns.has(columnKey)) return;
      visitedColumns.add(columnKey);

      const links = this.allColumnLinks.filter(link =>
        (link.sourceTableId === tableId && link.sourceColumnId === columnId) ||
        (link.targetTableId === tableId && link.targetColumnId === columnId)
      );

      links.forEach(link => {
        const linkKey = `${link.sourceTableId}:${link.sourceColumnId}->${link.targetTableId}:${link.targetColumnId}`;
        if (!linkKeys.has(linkKey)) {
          linkKeys.add(linkKey);
          collectedLinks.push(link);

          if (link.sourceTableId === tableId && link.sourceColumnId === columnId) {
            collectRecursive(link.targetTableId, link.targetColumnId);
          } else {
            collectRecursive(link.sourceTableId, link.sourceColumnId);
          }
        }
      });
    };

    collectRecursive(startTableId, startColumnId);
    return collectedLinks;
  }

  private showLinksForColumnAfterRender(tableId: string, columnId: number, columnName: string): void {
    this.selectedColumn = { tableId, columnId, columnName };
    this.linkDisplayMode = 'selected';

    const allRelevantLinks = this.collectAllRelatedLinks(tableId, columnId);

    // Определяем прямые связи (первый уровень) выбранного столбца
    const directColumnKeys = new Set<string>();
    allRelevantLinks.forEach(linkData => {
      const isDirectLink = 
        (linkData.sourceTableId === tableId && linkData.sourceColumnId === columnId) ||
        (linkData.targetTableId === tableId && linkData.targetColumnId === columnId);
      
      if (isDirectLink) {
        // Добавляем связанный столбец в прямые связи
        if (linkData.sourceTableId === tableId && linkData.sourceColumnId === columnId) {
          directColumnKeys.add(`${linkData.targetTableId}:${linkData.targetColumnId}`);
        } else {
          directColumnKeys.add(`${linkData.sourceTableId}:${linkData.sourceColumnId}`);
        }
      }
    });

    // Сначала сбрасываем подсветку всех портов
    this.clearPortHighlights();

    // Выделяем выбранный столбец зелёным
    this.highlightPort(tableId, columnId, 'selected');

    // Создаём связи и выделяем связанные столбцы
    allRelevantLinks.forEach(linkData => {
      const isDirectLink = 
        (linkData.sourceTableId === tableId && linkData.sourceColumnId === columnId) ||
        (linkData.targetTableId === tableId && linkData.targetColumnId === columnId);

      const link = this.createColumnLink(
        linkData.sourceTableId,
        linkData.sourceColumnId,
        linkData.targetTableId,
        linkData.targetColumnId,
        linkData.parentData
      );

      link.attr('line/stroke', '#7855ED');
      link.attr('line/strokeWidth', isDirectLink ? 3 : 2);
      link.attr('line/targetMarker/fill', '#7855ED');

      this.graph.addCell(link);
      const linkKey = `${linkData.sourceTableId}:${linkData.sourceColumnId}-${linkData.targetTableId}:${linkData.targetColumnId}`;
      this.createdLinks.set(linkKey, link);

      const sourceKey = `${linkData.sourceTableId}:${linkData.sourceColumnId}`;
      const targetKey = `${linkData.targetTableId}:${linkData.targetColumnId}`;
      const selectedKey = `${tableId}:${columnId}`;

      if (sourceKey !== selectedKey) {
        const highlightType = directColumnKeys.has(sourceKey) ? 'related' : 'related-related';
        this.highlightPort(linkData.sourceTableId, linkData.sourceColumnId, highlightType);
      }

      // Target столбец
      if (targetKey !== selectedKey) {
        const highlightType = directColumnKeys.has(targetKey) ? 'related' : 'related-related';
        this.highlightPort(linkData.targetTableId, linkData.targetColumnId, highlightType);
      }
    });
  }

  public showAllLinks(): void {
    this.linkDisplayMode = 'all';
    this.selectedColumn = null;
    this.isShowAllLinks = true;
    this.clearColumnLinks();

    // Используем Set для избежания дублей связей между столбцами
    const createdLinkKeys = new Set<string>();

    this.allColumnLinks.forEach(linkData => {
      // Создаём уникальный ключ для связи столбец-столбец
      const linkKey = `${linkData.sourceTableId}:${linkData.sourceColumnId}->${linkData.targetTableId}:${linkData.targetColumnId}`;
      
      if (createdLinkKeys.has(linkKey)) {
        return; // Пропускаем дубликат
      }
      createdLinkKeys.add(linkKey);

      const link = this.createColumnLink(
        linkData.sourceTableId,
        linkData.sourceColumnId,
        linkData.targetTableId,
        linkData.targetColumnId,
        linkData.parentData
      );
      this.graph.addCell(link);
    });

    this.clearPortHighlights();
  }

  public hideAllLinks(): void {
    this.linkDisplayMode = 'none';
    this.selectedColumn = null;
    this.isShowAllLinks = false;
    this.clearColumnLinks();
    this.clearPortHighlights();
  }

  public clearColumnLinks(): void {
    const links = this.graph.getLinks();
    links.forEach(link => {
      const source = link.source();
      const target = link.target();
      if (source.port || target.port) 
        link.remove();
    });
    this.createdLinks.clear();
  }

  private highlightPort(tableId: string, columnId: number, type: 'selected' | 'related' | 'related-related' | 'none'): void {
    const element = this.graph.getCell(tableId) as joint.dia.Element;
    if (!element) return;

    const portId = `column_${columnId}`;

    switch (type) {
      case 'selected':
        element.portProp(portId, 'attrs/portBody/fill', '#E8F5E9');
        element.portProp(portId, 'attrs/portBody/stroke', '#4CAF50');
        element.portProp(portId, 'attrs/portBody/strokeWidth', 2);
        break;
      case 'related':
        element.portProp(portId, 'attrs/portBody/fill', '#FFF3E0');
        element.portProp(portId, 'attrs/portBody/stroke', '#FF9800');
        element.portProp(portId, 'attrs/portBody/strokeWidth', 2);
        break;
      case 'related-related':
        element.portProp(portId, 'attrs/portBody/fill', '#FDCDFF');
        element.portProp(portId, 'attrs/portBody/stroke', '#FD4FFF');
        element.portProp(portId, 'attrs/portBody/strokeWidth', 2);
        break;
      case 'none':
      default:
        element.portProp(portId, 'attrs/portBody/fill', '#FAFAFA');
        element.portProp(portId, 'attrs/portBody/stroke', '#E5E5E5');
        element.portProp(portId, 'attrs/portBody/strokeWidth', 1);
        break;
    }
  }

  private clearPortHighlights(): void {
    this.graph.getElements().forEach(element => {
      const ports = element.getPorts();
      ports.forEach(port => {
        if (port.id) {
          element.portProp(port.id, 'attrs/portBody/fill', '#FAFAFA');
          element.portProp(port.id, 'attrs/portBody/stroke', '#E5E5E5');
          element.portProp(port.id, 'attrs/portBody/strokeWidth', 1);
        }
      });
    });
  }

  public incrementBackward(): void {
    this.nBackward = Math.max(0, this.nBackward + 1);
    this.loadFromApiAndBuild();
  }

  public decrementBackward(): void {
    this.nBackward = Math.max(0, this.nBackward - 1);
    this.loadFromApiAndBuild();
  }

  public incrementForward(): void {
    this.nForward = Math.max(0, this.nForward + 1);
    this.loadFromApiAndBuild();
  }

  public decrementForward(): void {
    this.nForward = Math.max(0, this.nForward - 1);
    this.loadFromApiAndBuild();
  }

  public loadFromApiAndBuild(requestPayload?: any): void {
    const payload = requestPayload ?? {
      object_type: this.elementType?.toLowerCase() || 'table',
      object_id: this.currentElement?.id || 0,
      n_forward: this.nForward,
      n_backward: this.nBackward,
      show_bi: this.isShowBI,
      show_unknown: this.isShowUnknown
    };

    this.isShowAllLinks = false;
    this.fetchAndBuildLineageFromApi(payload);
  }

  private fetchAndBuildLineageFromApi(payload: any): void {
    const objectType = (payload.object_type || 'table').toLowerCase();
    const biTypes = ['report', 'chart'];
    const isRequestingBI = biTypes.includes(objectType);

    if (isRequestingBI) {
       const biReq = {
        report_id: payload.object_id ?? this.tableId ?? 0,
        n_backward: payload.n_backward ?? 3,
        show_unknown: this.isShowUnknown
      };

      this.pythonService.getObjectBILineage(biReq).subscribe({
        next: (resp) => {
          const lineageData = this.parseLineageResponse(resp || {}, payload);
          this.renderLineage(lineageData);
        },
        error: (err) => {
          console.error('Error fetching BI lineage:', err);
          const lineageData = this.parseLineageResponse({}, payload);
          this.renderLineage(lineageData);
        }
      });
    } else {
      const dwhReq = {
        object_type: objectType,
        object_id: payload.object_id || this.tableId || 0,
        n_forward: payload.n_forward ?? 3,
        n_backward: payload.n_backward ?? 3,
        show_bi: payload.show_bi ?? false,
        show_unknown: this.isShowUnknown
      };

      this.pythonService.getObjectLineage(dwhReq).subscribe({
        next: (resp) => {
          const lineageData = this.parseLineageResponse(resp || {}, payload);
          this.renderLineage(lineageData);
        },
        error: (err) => {
          console.error('Error fetching DWH lineage:', err);
          const lineageData = this.parseLineageResponse({}, payload);
          this.renderLineage(lineageData);
        }
      });
    }
  }

  private parseLineageResponse(resp: any, payload: any): LineageData {
    const dwhItems: any[] = resp.dwh_lineages || [];
    const biItems: any[] = resp.bi_lineages || [];

    const tables = new Map<string, TableNodeData>();
    const tableLinks: TableLinkData[] = [];
    const columnLinks: ColumnLinkData[] = [];
    const tableLinkSet = new Set<string>(); // Для избежания дублей связей таблиц
    const columnLinkSet = new Set<string>(); // Для избежания дублей связей столбцов
    let unknownCounter = 0;

    dwhItems.forEach((item) => {
      const srcIdNum = item.src_object_id;
      const tgtIdNum = item.tgt_object_id;

      const srcIsUnknown = srcIdNum === null || srcIdNum === undefined;
      const tgtIsUnknown = tgtIdNum === null || tgtIdNum === undefined;

      const srcTableId = srcIsUnknown ? `unknown_${++unknownCounter}` : String(srcIdNum);
      const tgtTableId = tgtIsUnknown ? `unknown_${++unknownCounter}` : String(tgtIdNum);

      if (!tables.has(srcTableId)) {
        tables.set(srcTableId, {
          id: srcTableId,
          objectId: srcIdNum || 0,
          label: `${item.src_schema || ''}.${item.src_object_name || srcTableId}`,
          schema: item.src_schema || '',
          objectType: srcIsUnknown || item.src_object_type === null ? 'Отсутствующий' : (item.src_object_type || 'table'),
          columns: [],
          isMain: false
        });
      }

      if (!tables.has(tgtTableId)) {
        tables.set(tgtTableId, {
          id: tgtTableId,
          objectId: tgtIdNum || 0,
          label: `${item.tgt_schema || ''}.${item.tgt_object_name || tgtTableId}`,
          schema: item.tgt_schema || '',
          objectType: tgtIsUnknown || item.tgt_object_type === null ? 'Отсутствующий' : (item.tgt_object_type || 'table'),
          columns: [],
          isMain: false
        });
      }

      // Добавляем связь на уровне таблиц (включая unknown объекты)
      const tableLinkKey = `${srcTableId}->${tgtTableId}`;
      if (!tableLinkSet.has(tableLinkKey)) {
        tableLinkSet.add(tableLinkKey);
        tableLinks.push({
          sourceTableId: srcTableId,
          targetTableId: tgtTableId,
          parentData: item.parent_entity_id ? {
            id: String(item.parent_entity_id),
            name: item.parent_entity_name || '',
            type: item.parent_entity_type || '',
            schema: item.parent_entity_schema || ''
          } : undefined
        });
      }

      const srcTable = tables.get(srcTableId)!;
      const tgtTable = tables.get(tgtTableId)!;

      if (Array.isArray(item.column_lineage)) {
        item.column_lineage.forEach((colLink: any) => {
          if (colLink.src_column_name && colLink.src_column_id) {
            const existingCol = srcTable.columns.find(c => c.id === colLink.src_column_id);
            if (!existingCol) {
              srcTable.columns.push({
                id: colLink.src_column_id,
                name: colLink.src_column_name,
                type: colLink.src_column_type || 'NOT NULL'
          });
        }
      }
      
          if (colLink.tgt_column_name && colLink.tgt_column_id) {
            const existingCol = tgtTable.columns.find(c => c.id === colLink.tgt_column_id);
            if (!existingCol) {
              tgtTable.columns.push({
                id: colLink.tgt_column_id,
                name: colLink.tgt_column_name,
                type: colLink.tgt_column_type || 'NULL'
              });
            }
          }
      
          // Column links (для обоих - известных и неизвестных объектов)
          if (colLink.src_column_id && colLink.tgt_column_id) {
            const colLinkKey = `${srcTableId}:${colLink.src_column_id}->${tgtTableId}:${colLink.tgt_column_id}`;
            if (!columnLinkSet.has(colLinkKey)) {
              columnLinkSet.add(colLinkKey);
              columnLinks.push({
                sourceTableId: srcTableId,
                sourceColumnId: colLink.src_column_id,
                targetTableId: tgtTableId,
                targetColumnId: colLink.tgt_column_id,
                parentData: item.parent_entity_id ? {
                  id: String(item.parent_entity_id),
                  name: item.parent_entity_name || '',
                  type: item.parent_entity_type || '',
                  schema: item.parent_entity_schema || ''
                } : undefined
              });
            }
          }
        });
      }
    });

    biItems.forEach((item) => {
      const srcObjectId = item.src_object_id;
      const reportId = item.report_id;
      if (srcObjectId == null || reportId == null) return;

      const tableId = tables.has(String(srcObjectId)) ? String(srcObjectId) : `table_${srcObjectId}`;
      const reportNodeId = `report_${reportId}`;

      if (!tables.has(tableId)) {
        tables.set(tableId, {
          id: tableId,
          objectId: srcObjectId,
          label: `${item.src_schema || ''}.${item.src_object_name || srcObjectId}`,
          schema: item.src_schema || '',
          objectType: item.src_object_type || 'table',
          columns: [],
          isMain: false
        });
      }

      if (!tables.has(reportNodeId)) {
        tables.set(reportNodeId, {
          id: reportNodeId,
          objectId: reportId,
          label: item.report_name || `Report ${reportId}`,
          schema: '',
          objectType: 'report',
          columns: [],
          isMain: false
        });
      }

      // Добавляем связь таблица -> отчёт
      const tableLinkKey = `${tableId}->${reportNodeId}`;
      if (!tableLinkSet.has(tableLinkKey)) {
        tableLinkSet.add(tableLinkKey);
        tableLinks.push({
          sourceTableId: tableId,
          targetTableId: reportNodeId,
          parentData: item.chart_id ? {
            id: String(item.chart_id),
            name: item.chart_name || '',
            type: 'chart',
            schema: ''
          } : undefined
        });
      }
    });

    // Mark center node
    const centerNodeId = payload.object_id ? String(payload.object_id) : null;
    if (centerNodeId && tables.has(centerNodeId)) {
      tables.get(centerNodeId)!.isMain = true;
    } else if (payload.object_type === 'report' && payload.object_id) {
      const reportNodeId = `report_${payload.object_id}`;
      if (tables.has(reportNodeId)) {
        tables.get(reportNodeId)!.isMain = true;
      }
    }

    // Если нет данных линейджа, создаём узел для текущего объекта
    if (tables.size === 0 && this.currentElement) {
      const objectId = String(this.currentElement.id);
      const objectType = (payload.object_type || this.elementType || 'table').toLowerCase();
      const isReport = objectType === 'report';
      const nodeId = isReport ? `report_${objectId}` : objectId;
      
      tables.set(nodeId, {
        id: nodeId,
        objectId: this.currentElement.id,
        label: this.currentElement.name || `${this.currentElement.schema || ''}.${objectId}`,
        schema: this.currentElement.schema || '',
        objectType: objectType,
        columns: [],
        isMain: true
      });
    }

    return {
      tables: Array.from(tables.values()),
      tableLinks: tableLinks,
      columnLinks: columnLinks
    };
  }

  private renderLineage(data: LineageData): void {
    // Store for re-rendering when toggling node columns
    this.currentLineageData = data;
    
    this.graph.clear();
    this.allColumnLinks = data.columnLinks;
    this.createdLinks.clear();
    this.selectedColumn = null;
    this.linkDisplayMode = 'none';

    if (data.tables.length === 0) {
      console.warn('No tables to render');
      return;
    }

    // Step 1: Calculate layout using Cytoscape + Dagre
    const positions = this.calculateLayoutWithCytoscape(data);

    // Step 2: Create JointJS nodes with calculated positions
    const nodeElements: joint.shapes.standard.Rectangle[] = [];

    data.tables.forEach((table) => {
      const pos = positions.get(table.id) || { x: 100, y: 100 };
      const node = this.createTableNode(table, pos);
      nodeElements.push(node);
      this.graph.addCell(node);
    });

    // Step 3: Create table-level links (always show them)
    this.createTableLevelLinksFromData(data.tableLinks);

    // Step 4: Center content in viewport
    this.centerContent();
  }

  private centerContent(): void {
    setTimeout(() => {
      const bbox = this.graph.getBBox();
      if (!bbox) return;
      
      const container = this.cyContainer.nativeElement;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      if (containerWidth === 0 || containerHeight === 0) return;
      
      const contentWidth = bbox.width;
      const contentHeight = bbox.height;
      
      if (contentWidth === 0 || contentHeight === 0) return;
      
      const padding = 60;
      const availableWidth = containerWidth - padding * 2;
      const availableHeight = containerHeight - padding * 2;
      
      const scaleX = availableWidth / contentWidth;
      const scaleY = availableHeight / contentHeight;
      
      let scale = Math.min(scaleX, scaleY);
      
      scale = Math.max(0.2, Math.min(1.5, scale));
      
      const bboxCenterX = bbox.x + contentWidth / 2;
      const bboxCenterY = bbox.y + contentHeight / 2;
      
      const screenCenterX = containerWidth / 2;
      const screenCenterY = containerHeight / 2;
      
      const tx = screenCenterX - bboxCenterX * scale;
      const ty = screenCenterY - bboxCenterY * scale;

      this.zoomLevel = scale;
      this.panX = tx;
      this.panY = ty;
      
      this.paper.matrix({
        a: scale,
        b: 0,
        c: 0,
        d: scale,
        e: tx,
        f: ty
      });
    }, 150);
  }

  private calculateLayoutWithCytoscape(data: LineageData): Map<string, { x: number; y: number }> {
    // Create temporary container for Cytoscape
    const tempContainer = document.createElement('div');
    tempContainer.style.width = '1px';
    tempContainer.style.height = '1px';
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    document.body.appendChild(tempContainer);

    const columnRowHeight = 26;
    const bottomPadding = 15;
    const baseNodeWidth = 280;
    const charWidth = 7;
    const typePadding = 60;
    const sidePadding = 30;

    const nodeHeights = new Map<string, number>();
    
    const nodes = data.tables.map(t => {
      const isExpanded = this.expandedNodes.has(t.id);
      const needsWrap = t.label.length > 30;
      const headerHeight = needsWrap ? 60 : 50;
      
      const nodeHeight = isExpanded
        ? Math.max(80, headerHeight + t.columns.length * columnRowHeight + bottomPadding)
        : (needsWrap ? 70 : 60);
      
      let nodeWidth = baseNodeWidth;
      if (isExpanded && t.columns.length > 0) {
        const maxNameWidth = Math.max(...t.columns.map(col => col.name.length * charWidth));
        nodeWidth = Math.max(maxNameWidth + typePadding + sidePadding, baseNodeWidth);
      }
      
      nodeHeights.set(t.id, nodeHeight);
      
      return {
          data: {
          id: t.id,
          width: nodeWidth,
          height: nodeHeight
        }
      };
    });

    const edges = data.tableLinks.map((link, idx) => ({
      data: {
        id: `edge_${idx}`,
        source: link.sourceTableId,
        target: link.targetTableId
      }
    }));

    const cy = cytoscape({
      container: tempContainer,
      elements: [...nodes, ...edges],
      headless: true,
      style: [
        {
          selector: 'node',
          style: {
            'width': 'data(width)',
            'height': 'data(height)'
          }
        }
      ]
    });

    const baseNodeSep = 100;
    const rankSep = 400;

    const layout = cy.layout({
      name: 'dagre',
      // @ts-ignore - cytoscape-dagre specific options
      rankDir: 'LR',
      nodeSep: baseNodeSep,
      rankSep: rankSep,
      edgeSep: 10,
      ranker: 'network-simplex',
      align: undefined
    } as any);

    layout.run();

    const positions = new Map<string, { x: number; y: number }>();
    cy.nodes().forEach((node: any) => {
      const pos = node.position();
      positions.set(node.id(), { x: pos.x, y: pos.y });
    });

    cy.destroy();
    document.body.removeChild(tempContainer);

    this.fixNodeOverlaps(positions, nodeHeights, baseNodeWidth);

    return positions;
  }

  private fixNodeOverlaps(
    positions: Map<string, { x: number; y: number }>,
    nodeHeights: Map<string, number>,
    nodeWidth: number
  ): void {
    const fixedSpacing = 50;
    
    // Группируем узлы по уровням (округляем X координату для группировки)
    const levelTolerance = nodeWidth * 0.5; // Узлы на одном уровне, если X отличается не более чем на 50% ширины
    const levels = new Map<number, Array<{ id: string; height: number }>>();
    
    positions.forEach((pos, id) => {
      const level = Math.round(pos.x / levelTolerance) * levelTolerance;
      if (!levels.has(level)) {
        levels.set(level, []);
      }
      const height = nodeHeights.get(id) || 60;
      levels.get(level)!.push({ id, height });
    });

    // Для каждого уровня сортируем узлы по Y и позиционируем последовательно
    levels.forEach((nodes) => {
      // Сортируем узлы по Y координате
      nodes.sort((a, b) => {
        const posA = positions.get(a.id);
        const posB = positions.get(b.id);
        return (posA?.y || 0) - (posB?.y || 0);
      });

      // Позиционируем каждый следующий узел относительно предыдущего
      // Используем высоту предыдущего узла + фиксированный отступ
      for (let i = 0; i < nodes.length - 1; i++) {
        const current = nodes[i];
        const next = nodes[i + 1];
        const currentPos = positions.get(current.id);
        const nextPos = positions.get(next.id);
        
        if (!currentPos || !nextPos) continue;
        
        // Вычисляем нижний край текущего узла
        const currentBottom = currentPos.y + current.height / 2;
        
        // Новая Y позиция для следующего узла:
        // нижний край предыдущего + фиксированный отступ + половина высоты следующего узла
        const requiredNextY = currentBottom + fixedSpacing + next.height / 2;
        
        // Если следующий узел выше требуемой позиции, сдвигаем его и все последующие
        if (nextPos.y < requiredNextY) {
          const shift = requiredNextY - nextPos.y;
          for (let j = i + 1; j < nodes.length; j++) {
            const nodePos = positions.get(nodes[j].id);
            if (nodePos) {
              nodePos.y += shift;
            }
          }
        }
      }
    });
  }

  private createTableLevelLinksFromData(tableLinks: TableLinkData[]): void {
    tableLinks.forEach(linkData => {
      const link = new joint.shapes.standard.Link({
        source: { id: linkData.sourceTableId },
        target: { id: linkData.targetTableId },
        attrs: {
          line: {
            stroke: '#D0ADFF',
            strokeWidth: 2,
            strokeDasharray: linkData.parentData ? '5,3' : 'none',
            targetMarker: {
              type: 'path',
              d: 'M 10 -5 0 0 10 5 z',
              fill: '#D0ADFF'
            }
          }
        },
        router: {
          name: 'manhattan', args: {
            padding: 25, step: 25, margin: 15 } },
        connector: { name: 'rounded', args: { radius: 5 } }
      });

      if (linkData.parentData) {
        link.prop('parentData', linkData.parentData);
        link.appendLabel({
          attrs: {
            text: {
              text: linkData.parentData.name, fontSize: 9, fill: '#666', fontFamily: 'Inter, sans-serif', },
            rect: { fill: '#fff', stroke: '#E5E5E5', strokeWidth: 1, rx: 3, ry: 3 }
          },
          position: { distance: 0.5 }
        });
      }

      this.graph.addCell(link);
    });
  }

  private createTableNode(table: TableNodeData, pos: { x: number; y: number }): joint.shapes.standard.Rectangle {
    const isUnknown = table.objectType === 'Отсутствующий';
    const isBI = table.objectType === 'report';
    const isExpanded = this.expandedNodes.has(table.id);
    const hasColumns = table.columns && table.columns.length > 0;

    // Определяем столбцы, участвующие в связях (как source или target)
    const linkedColumnIds = new Set<number>();
    this.allColumnLinks.forEach(link => {
      if (link.sourceTableId === table.id) {
        linkedColumnIds.add(link.sourceColumnId);
      }
      if (link.targetTableId === table.id) {
        linkedColumnIds.add(link.targetColumnId);
      }
    });

    const ports = isExpanded ? table.columns.map((col) => ({
      id: `column_${col.id}`,
      group: 'columns',
      args: { columnData: col, hasLink: linkedColumnIds.has(col.id) }
    })) : [];

    // Расчёт высоты: заголовок + столбцы (26px каждый) + отступ снизу (15px)
    const columnRowHeight = 26;
    const bottomPadding = 15;
    
    const headerHeight = 50;
    
    const nodeHeight = isExpanded
      ? Math.max(80, headerHeight + table.columns.length * columnRowHeight + bottomPadding)
      : 70;

    const node = new joint.shapes.standard.Rectangle({
      id: table.id,
      position: { x: pos.x - 140, y: pos.y - nodeHeight / 2 },
      size: { width: 280, height: nodeHeight },
      markup: [
        { tagName: 'rect', selector: 'body' },
        { tagName: 'text', selector: 'label' },
        // Кнопка раскрыть/скрыть столбцы
        { tagName: 'rect', selector: 'expandButton' },
        { tagName: 'text', selector: 'expandIcon' },
        // Кнопка перехода к контенту
        { tagName: 'rect', selector: 'navigateButton' },
        { tagName: 'text', selector: 'navigateIcon' }
      ],
      attrs: {
        body: {
          fill: isUnknown ? '#ffebee' : (isBI ? '#E3F2FD' : (table.isMain ? '#F2EEFF' : '#FFFFFF')),
          stroke: isUnknown ? 'red' : (isBI ? '#2196F3' : (table.isMain ? '#7855ED' : '#E5E5E5')),
          strokeWidth: table.isMain ? 3 : 2,
          rx: 10,
          ry: 10,
          filter: {
            name: 'dropShadow',
            args: { dx: 0, dy: 2, blur: 4, color: 'rgba(0,0,0,0.1)' }
          }
        },
        label: {
          text: table.label,
          fill: isUnknown ? 'red' : (isBI ? '#2196F3' : (table.isMain ? '#7855ED' : '#333')),
          fontSize: 13,
          fontWeight: table.isMain ? 'bold' : '600',
          refX: '50%',
          refY: isExpanded ? 25 : '50%',
          textAnchor: 'middle',
          fontFamily: 'Inter, sans-serif',
          textWrap: {
            width: 240,
            ellipsis: true
          }
        },
        expandButton: {
          width: 22,
          height: 22,
          x: 250,
          y: 5,
          rx: 4,
          ry: 4,
          fill: hasColumns ? '#F0F0F0' : '#FAFAFA',
          stroke: hasColumns ? '#CCC' : '#E5E5E5',
          strokeWidth: 1,
          cursor: hasColumns ? 'pointer' : 'default',
          visibility: hasColumns ? 'visible' : 'hidden',
          event: 'element:expand:click'
        },
        expandIcon: {
          text: isExpanded ? '▲' : '▼',
          x: 261,
          y: 21,
          fontSize: 10,
          fill: '#666',
          textAnchor: 'middle',
          fontFamily: 'Arial, sans-serif',
          cursor: hasColumns ? 'pointer' : 'default',
          visibility: hasColumns ? 'visible' : 'hidden',
          pointerEvents: 'none'
        },
        navigateButton: {
          width: 22,
          height: 22,
          x: 8,
          y: 5,
          rx: 4,
          ry: 4,
          fill: isUnknown ? '#FAFAFA' : '#F7F7F7',
          stroke: isUnknown ? '#E5E5E5' : '#F7F7F7',
          strokeWidth: 1,
          cursor: isUnknown ? 'default' : 'pointer',
          visibility: isUnknown ? 'hidden' : 'visible',
          event: 'element:navigate:click'
        },
        navigateIcon: {
          text: '📄',
          x: 19,
          y: 21,
          fontSize: 14,
          fill: '#4CAF50',
          textAnchor: 'middle',
          fontFamily: 'Arial, sans-serif',
          cursor: isUnknown ? 'default' : 'pointer',
          visibility: isUnknown ? 'hidden' : 'visible',
          pointerEvents: 'none'
        }
      },
      ports: {
        groups: {
          columns: {
            position: {
              name: 'absolute',
              args: {}
            },
            attrs: {
              portBody: {
                magnet: true,
                width: 260,
                height: 22,
                x: 10,
                y: 0,
                fill: '#FAFAFA',
                stroke: '#E5E5E5',
                strokeWidth: 1,
                rx: 4,
                ry: 4,
                cursor: 'pointer',
                event: 'element:port:click',
                pointerEvents: 'all'
              },
              portLabel: {
                fontSize: 11,
                fill: '#333',
                fontFamily: 'Monaco, monospace',
                x: 15,
                y: 15,
                textAnchor: 'start',
                pointerEvents: 'none'
              },
              portType: {
                fontSize: 9,
                fill: '#999',
                fontFamily: 'Inter, sans-serif',
                x: 255,
                y: 15,
                textAnchor: 'end',
                pointerEvents: 'none'
              }
            },
            markup: [
              { tagName: 'rect', selector: 'portBody' },
              { tagName: 'text', selector: 'portLabel' },
              { tagName: 'text', selector: 'portType' }
            ]
          }
        },
        items: ports
      }
    });

    node.prop('nodeType', table.isMain ? 'main' : 'source');
    node.prop('objectType', table.objectType);
    node.prop('objectId', table.objectId);
    node.prop('schema', table.schema);
    node.prop('columnsList', table.columns);
    node.prop('hasColumns', hasColumns);

    if (isExpanded) {
      const charWidth = 7;
      const typePadding = 60;
      const sidePadding = 30;
      
      let maxNameWidth = 0;
      table.columns.forEach(col => {
        maxNameWidth = Math.max(maxNameWidth, col.name.length * charWidth);
      });
      
      const newNodeWidth = Math.max(maxNameWidth + typePadding + sidePadding, 280);
      const portBodyWidth = newNodeWidth - 20;
      
      table.columns.forEach((col, index) => {
        const portId = `column_${col.id}`;
        const portY = headerHeight + index * columnRowHeight;
        const hasLink = linkedColumnIds.has(col.id);
        
        node.portProp(portId, 'args/y', portY);
        node.portProp(portId, 'attrs/portLabel/text', col.name);
        node.portProp(portId, 'attrs/portType/text', col.type);
        
        // Обновляем ширину порта и позицию типа
        node.portProp(portId, 'attrs/portBody/width', portBodyWidth);
        node.portProp(portId, 'attrs/portType/x', portBodyWidth + 5);
        
        if (!hasLink) {
          node.portProp(portId, 'attrs/portLabel/fill', '#BFBFBF');
        }
      });
      node.resize(newNodeWidth, nodeHeight);
      
      node.attr('expandButton/x', newNodeWidth - 30);
      node.attr('expandIcon/x', newNodeWidth - 19);
    }

    return node;
  }

  private createColumnLink(
    sourceTableId: string,
    sourceColumnId: number,
    targetTableId: string,
    targetColumnId: number,
    parentData?: any
  ): joint.shapes.standard.Link {
    const link = new joint.shapes.standard.Link({
      source: {
        id: sourceTableId,
        port: `column_${sourceColumnId}`,
        anchor: { name: 'right', args: { dy: 0 } }
      },
      target: {
        id: targetTableId,
        port: `column_${targetColumnId}`,
        anchor: { name: 'left', args: { dy: 0 } }
      },
      attrs: {
        line: {
          stroke: '#D0ADFF',
          strokeWidth: 2,
          strokeDasharray: parentData ? '5,3' : 'none',
          targetMarker: {
            type: 'path',
            d: 'M 10 -5 0 0 10 5 z',
            fill: parentData ? '#FFA726' : '#D0ADFF'
          }
        }
      },
      router: {
        name: 'rightAngle',
      },
      connector: { name: 'rounded', args: { radius: 8 } }
    });

    if (parentData) {
      link.prop('parentData', parentData);
    }

    return link;
  }


  toggleShowColumns(): void {
    this.showColumns = !this.showColumns;
    this.linkDisplayMode = 'none';
    this.selectedColumn = null;
    
    // При глобальном переключении обновляем состояние всех узлов
    if (this.showColumns) {
      // Раскрыть все узлы
      this.currentLineageData?.tables.forEach((table: TableNodeData) => {
        if (table.columns && table.columns.length > 0) {
          this.expandedNodes.add(table.id);
        }
      });
          } else {
      // Скрыть все столбцы
      this.expandedNodes.clear();
    }
    
    this.loadFromApiAndBuild();
  }

  toggleNodeColumns(nodeId: string): void {
    if (this.expandedNodes.has(nodeId)) {
      this.expandedNodes.delete(nodeId);
            } else {
      this.expandedNodes.add(nodeId);
    }

    // Обновляем showColumns флаг если хотя бы один узел развёрнут
    this.showColumns = this.expandedNodes.size > 0;
    
    // Перерисовываем без перезагрузки данных
    if (this.currentLineageData) {
      this.renderLineage(this.currentLineageData);
    }
  }

  expandAllNodes(): void {
    if (!this.currentLineageData) return;
    
    this.currentLineageData.tables.forEach((table: TableNodeData) => {
      if (table.columns && table.columns.length > 0) {
        this.expandedNodes.add(table.id);
      }
    });
    
    this.showColumns = this.expandedNodes.size > 0;
    this.renderLineage(this.currentLineageData);
  }

  collapseAllNodes(): void {
    this.expandedNodes.clear();
    // Не сбрасываем showColumns чтобы галочка оставалась активной
    
    if (this.currentLineageData) {
      this.renderLineage(this.currentLineageData);
    }
  }

  public isShowAllLinks: boolean = false; 

  expandAllAndShowLinks(): void {
    if (!this.currentLineageData) return;

    this.isShowAllLinks = !this.isShowAllLinks;

    if (this.isShowAllLinks) {

      this.currentLineageData.tables.forEach((table: TableNodeData) => {
        if (table.columns && table.columns.length > 0) {
          this.expandedNodes.add(table.id);
        }
      });

      this.showColumns = this.expandedNodes.size > 0;
      this.renderLineage(this.currentLineageData);

      setTimeout(() => {
        this.showAllLinks();
      }, 100);
    }

    else {
      this.hideAllLinks();
    }
  }

  toggleShowBI(): void {
    this.isShowBI = !this.isShowBI;
    this.loadFromApiAndBuild();
  }

  toggleShowUnknownObjects(): void {
    this.isShowUnknown = !this.isShowUnknown;
    this.loadFromApiAndBuild();
  }

  toggleToolbar(): void {
    this.isToolbarCollapsed = !this.isToolbarCollapsed;
    setTimeout(() => {
      if (this.paper) {
        this.paper.setDimensions('100%', '100%');
      }
    }, 300);
  }

  goBack(): void {
    if (this.currentElement && this.elementType) {
      this.router.navigate(['/content']).then(() => {
        this.sessionService.setItem('currentElement', this.currentElement);
        this.sessionService.setItem('elementType',
          this.elementType.charAt(0).toUpperCase() + this.elementType.slice(1));
      });
    } else {
      this.router.navigate(['/content']);
    }
  }

  goToObject(nodeId: string): void {
    if (nodeId.startsWith('unknown_')) {
        return;
      }
      
    const node = this.graph.getCell(nodeId) as joint.dia.Element;
    if (!node) return;

    const nodeLabel = node.attr('label/text') || nodeId;
    const objectType = node.prop('objectType') || 'table';
    const schema = node.prop('schema');

    if (objectType === 'Отсутствующий') {
      return;
    }

    let objectId: number;
    let finalObjectType = objectType;

    if (nodeId.startsWith('table_')) {
      objectId = parseInt(nodeId.replace('table_', ''), 10);
    } else if (nodeId.startsWith('chart_')) {
      return;
    } else if (nodeId.startsWith('report_')) {
      objectId = parseInt(nodeId.replace('report_', ''), 10);
      finalObjectType = 'Report';
      } else {
      objectId = parseInt(nodeId, 10);
    }

    if (isNaN(objectId)) {
      console.error('Could not extract object ID from nodeId:', nodeId);
      return;
    }

    const currentDataSource = this.sessionService.getItem('currentDataSource');

    const element = {
      id: objectId,
      name: nodeLabel,
      schemaId: this.schemaId || this.sessionService.getItem('currentElement')?.schemaId,
      schema: schema,
      dataSourceId: this.dataSourceId || currentDataSource?.id
    };

    this.currentElement = element;
    this.elementType = finalObjectType;
    this.goBack();
  }

  private navigateToContent(nodeId: string, objectId: number, objectType: string, nodeLabel: string, schema: string): void {
    const currentDataSource = this.sessionService.getItem('currentDataSource');
    const objectTypeLower = objectType.toLowerCase();
    
    // Определяем тип элемента для content_db
    let elementType = objectType.charAt(0).toUpperCase() + objectType.slice(1);
    const biTypes = ['report', 'chart'];
    const isBI = biTypes.includes(objectTypeLower) || nodeId.startsWith('report_');
    
    if (isBI) {
      elementType = 'Report';
    }

    // Извлекаем имя объекта из label (формат: "schema.name")
    let objectName = nodeLabel;
    let objectSchema = schema;
    if (nodeLabel.includes('.')) {
      const parts = nodeLabel.split('.');
      if (parts.length >= 2) {
        objectSchema = parts[0] || schema;
        objectName = parts.slice(1).join('.');
      }
    }

    const element = {
      id: objectId,
      name: objectName,
      schema: objectSchema,
      schemaId: this.schemaId || this.sessionService.getItem('currentElement')?.schemaId,
      dataSourceId: this.dataSourceId || currentDataSource?.id,
      type: elementType
    };

    this.sessionService.setItem('currentElement', element);
    this.sessionService.setItem('elementType', elementType);
    this.sessionService.setItem('isBI', isBI);
    
    this.router.navigate(['/content']);
  }
}
